import * as React from 'react';

/**
 * Reactive localStorage hook with a `useState`-like signature.
 * JSON-serializes on write, JSON-parses on initial read; falls back to `initialValue`
 * on parse errors or when localStorage is unavailable (private mode, SSR).
 * Note: this hook is *not* cross-tab — other tabs writing the same key won't update.
 */
export function useLocalStorage<T>(key: string, initialValue: T) {
  const [storedValue, setStoredValue] = React.useState<T>(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch {
      return initialValue;
    }
  });

  const setValue = (value: T | ((val: T) => T)) => {
    try {
      const valueToStore = value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch {
      /* Ignore write errors */
    }
  };

  return [storedValue, setValue] as const;
}
